import { usePersistedState } from "@/lib/persistedFilters";
// Timeline tab — the whole tracked day in one screen: per-person activity
// bands (coloured by OUR classification), the session summary (start/end,
// active/productive), every break, and the apps behind the bands one click
// down. Live-fetched and cached per day on the backend.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ChevronDown, ChevronLeft, ChevronRight, Coffee, Loader2, RefreshCw, Search } from "lucide-react";
import { DETAILS, axisHour, bucketize, BandTrack, TimelineLegend } from "@/components/prodTimelineShared";
import { ProdTimelineDetail } from "@/components/ProdTimelineDetail";

const pad = (n) => String(n).padStart(2, "0");
const addDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const prettyDate = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
};
const SORTS = [
  { id: "productive", label: "Productive" },
  { id: "name", label: "Name" },
  { id: "start", label: "Start" },
  { id: "breaks", label: "Breaks" },
];

function Sparkline({ data }) {
  const max = Math.max(1, ...data);
  const w = 60, h = 16, step = w / (data.length - 1 || 1);
  const pts = data.map((v, i) => `${(i * step).toFixed(1)},${(h - (v / max) * h).toFixed(1)}`).join(" ");
  return (
    <svg width={w} height={h} className="text-sky-500" aria-hidden>
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    </svg>
  );
}

function DayStat({ label, value, sub, testid }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900" data-testid={testid}>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-lg font-semibold text-slate-800 dark:text-slate-100">{value}</div>
      {sub ? <div className="text-[11px] text-slate-400">{sub}</div> : null}
    </div>
  );
}

export default function ProdDayTimeline({ team = "" }) {
  const [date, setDate] = useState(null);
  const [today, setToday] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showIdle, setShowIdle] = useState(false);
  const [detail, setDetail] = useState("raw");
  const [win, setWin] = useState([8, 17]);
  const [q, setQ] = usePersistedState("prod-day.q", "");
  const [sort, setSort] = usePersistedState("prod-day.sort", "productive");
  const [open, setOpen] = useState("");
  const [days, setDays] = useState({});
  const tok = useRef(0);

  const load = useCallback(async (d, refresh) => {
    const t = ++tok.current;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (d) params.set("date", d);
      if (refresh) params.set("refresh", "1");
      const { data: res } = await api.get(`/productivity/day-timeline?${params.toString()}`);
      if (t !== tok.current) return;
      setData(res);
      setDate(res.date);
      setToday((prev) => prev || res.date);
      setOpen("");
      if (refresh) setDays({});
    } catch (e) {
      if (t === tok.current) toast.error(e.response?.data?.detail || "Could not load the timeline");
    } finally {
      if (t === tok.current) setLoading(false);
    }
  }, []);

  useEffect(() => { load(null, false); }, [load]);

  const toggle = async (key) => {
    if (open === key) { setOpen(""); return; }
    setOpen(key);
    const id = `${key}|${date}`;
    if (days[id]) return;
    try {
      const { data: d } = await api.get(`/productivity/users/${key}/day?date=${date}`);
      setDays((cur) => ({ ...cur, [id]: d }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open that day");
    }
  };

  const detailMin = DETAILS.find((d) => d.id === detail)?.min || 0;
  const winStart = win[0] * 3600;
  const winEnd = win[1] * 3600;
  const hourTicks = useMemo(() => {
    const out = [];
    for (let h = Math.ceil(win[0]); h <= Math.floor(win[1]); h++) out.push(h);
    return out;
  }, [win]);

  const rows = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    return data.people
      .filter((p) => (!team || p.team === team)
        && (!needle || p.name.toLowerCase().includes(needle) || (p.computer || "").toLowerCase().includes(needle)))
      .map((p) => ({ ...p, view: bucketize(p.bands, detailMin, showIdle) }))
      .sort((a, b) => {
        if (sort === "name") return a.name.localeCompare(b.name);
        if (sort === "start") return (a.real_start || a.sess_start || "99:99").localeCompare(b.real_start || b.sess_start || "99:99");
        if (sort === "breaks") return b.break_seconds - a.break_seconds || b.break_count - a.break_count;
        return b.productive_seconds - a.productive_seconds;
      });
  }, [data, detailMin, showIdle, q, team, sort]);

  const t = data?.totals;
  const canNext = today && date && date < today;

  return (
    <div data-testid="prod-timeline">
      {/* Controls */}
      <div className="mb-3 flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="flex items-center gap-1">
          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => load(addDays(date, -1), false)}
            disabled={loading || !date} data-testid="prod-timeline-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="min-w-[9rem] text-center text-sm font-semibold" data-testid="prod-timeline-date">
            {prettyDate(date)}{date === today ? " · Today" : ""}
          </span>
          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => load(addDays(date, 1), false)}
            disabled={loading || !canNext} data-testid="prod-timeline-next"><ChevronRight className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => load(date, true)}
            disabled={loading} title="Refresh from the tool" data-testid="prod-timeline-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>

        <div className="relative w-52">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a person"
            className="h-8 pl-8 text-sm" data-testid="prod-timeline-search" />
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
          <Switch checked={showIdle} onCheckedChange={setShowIdle} data-testid="prod-timeline-idle" />
          Show idle time periods
        </label>

        <div className="flex min-w-[220px] flex-1 items-center gap-3">
          <span className="whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">Working hours</span>
          <span className="w-14 text-right text-xs font-medium text-slate-500">{axisHour(win[0])}</span>
          <SliderPrimitive.Root value={win} onValueChange={setWin} min={0} max={24} step={1} minStepsBetweenThumbs={1}
            className="relative flex h-5 flex-1 touch-none select-none items-center" data-testid="prod-timeline-hours">
            <SliderPrimitive.Track className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
              <SliderPrimitive.Range className="absolute h-full bg-sky-500" />
            </SliderPrimitive.Track>
            <SliderPrimitive.Thumb className="block h-4 w-4 rounded-full border border-sky-500 bg-white shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400" />
            <SliderPrimitive.Thumb className="block h-4 w-4 rounded-full border border-sky-500 bg-white shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400" />
          </SliderPrimitive.Root>
          <span className="w-14 text-xs font-medium text-slate-500">{axisHour(win[1])}</span>
        </div>

        <div className="inline-flex overflow-hidden rounded-md border border-slate-200 dark:border-slate-700">
          {DETAILS.map((d) => (
            <button key={d.id} type="button" onClick={() => setDetail(d.id)}
              className={`px-2.5 py-1 text-xs font-semibold transition-colors ${detail === d.id
                ? "bg-sky-500 text-white"
                : "bg-white text-slate-600 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"}`}
              data-testid={`prod-timeline-detail-${d.id}`}>{d.label}</button>
          ))}
        </div>
      </div>

      {/* Day at a glance */}
      {t && (
        <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-6" data-testid="prod-timeline-totals">
          <DayStat label="Tracked" value={t.people} sub={team ? team : "people with activity"} testid="prod-timeline-stat-people" />
          <DayStat label="Active" value={t.active_hms} sub="real input at the machine" testid="prod-timeline-stat-active" />
          <DayStat label="Productive" value={t.productive_hms} sub={t.prod_pct == null ? "" : `${t.prod_pct}% of active`} testid="prod-timeline-stat-productive" />
          <DayStat label="Breaks" value={t.break_count} sub={t.break_hms ? `${t.break_hms} in total` : ""} testid="prod-timeline-stat-breaks" />
          <DayStat label="First in" value={t.first_in || "—"} testid="prod-timeline-stat-first" />
          <DayStat label="Last out" value={t.last_out || "—"} testid="prod-timeline-stat-last" />
        </div>
      )}

      {/* Legend + sort */}
      <div className="mb-2 flex flex-wrap items-center gap-3">
        <TimelineLegend showIdle={showIdle} />
        <div className="ml-auto inline-flex items-center gap-1.5 text-xs text-slate-500">
          Sort
          <div className="inline-flex overflow-hidden rounded-md border border-slate-200 dark:border-slate-700">
            {SORTS.map((s) => (
              <button key={s.id} type="button" onClick={() => setSort(s.id)}
                className={`px-2 py-0.5 text-[11px] font-semibold transition-colors ${sort === s.id
                  ? "bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900"
                  : "bg-white text-slate-600 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"}`}
                data-testid={`prod-timeline-sort-${s.id}`}>{s.label}</button>
            ))}
          </div>
        </div>
      </div>

      <Card className="overflow-x-auto p-0">
        <div className="min-w-[980px]">
          {/* Header + time axis */}
          <div className="flex items-end border-b border-slate-100 bg-slate-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:bg-slate-800/60 dark:text-slate-400">
            <div className="w-40 shrink-0">User</div>
            <div className="w-32 shrink-0">Computer</div>
            <div className="w-12 shrink-0 text-right">Prod</div>
            <div className="relative mx-3 h-4 flex-1">
              {hourTicks.map((h) => (
                <span key={h} className="absolute -translate-x-1/2 whitespace-nowrap"
                  style={{ left: `${((h * 3600 - winStart) / Math.max(1, winEnd - winStart)) * 100}%` }}>
                  {axisHour(h)}
                </span>
              ))}
            </div>
            <div className="w-24 shrink-0 text-right">Start–End</div>
            <div className="w-20 shrink-0 text-right">Breaks</div>
            <div className="w-40 shrink-0 pl-3">Active / Productive</div>
          </div>

          {/* Rows */}
          {loading && !data && (
            <div className="flex items-center justify-center gap-2 py-16 text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Fetching the day from the tool…
            </div>
          )}
          {data && rows.length === 0 && (
            <div className="py-16 text-center text-sm text-slate-500" data-testid="prod-timeline-empty">
              {data.people.length ? "Nobody matches that filter." : "No tracked activity recorded for this day."}
            </div>
          )}
          {rows.map((p) => (
            <div key={p.key} className="border-b border-slate-50 last:border-0 dark:border-slate-800/60">
              <button type="button" onClick={() => toggle(p.key)}
                className="flex w-full items-center px-3 py-2 text-left hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
                data-testid={`prod-timeline-row-${p.key}`}>
                <div className="w-40 shrink-0 truncate">
                  <div className="flex items-center gap-1 font-medium text-slate-800 dark:text-slate-100">
                    <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform ${open === p.key ? "" : "-rotate-90"}`} />
                    <span className="truncate">{p.name}</span>
                  </div>
                  <div className="truncate pl-[18px] text-[11px] text-slate-400">{p.team}</div>
                </div>
                <div className="w-32 shrink-0 truncate text-xs text-slate-500 dark:text-slate-400">{p.computer || "—"}</div>
                <div className="w-12 shrink-0 text-right text-sm font-semibold text-emerald-600 dark:text-emerald-400">{p.prod_pct == null ? "—" : p.prod_pct}</div>
                <div className="mx-3 flex-1">
                  {p.bands.length ? <BandTrack bands={p.view} winStart={winStart} winEnd={winEnd} />
                    : <div className="h-6 w-full rounded bg-slate-50 dark:bg-slate-800/40" />}
                  {p.longest_idle_seconds >= 900 && (
                    <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400"
                      title="Longest continuous idle stretch during the working session"
                      data-testid={`prod-timeline-break-${p.key}`}>
                      <Coffee className="h-3 w-3" /> Longest break {p.longest_idle_hms} @ {p.longest_idle_start}
                    </div>
                  )}
                </div>
                <div className="w-24 shrink-0 text-right font-mono text-[11px] text-slate-500 dark:text-slate-400"
                  title={p.sess_start && (p.real_start !== p.sess_start || p.real_end !== p.sess_end)
                    ? `Machine on ${p.sess_start}–${p.sess_end}; trimmed to the hours with real activity.` : ""}>
                  {p.sess_start && p.sess_end
                    ? <>{p.real_start || p.sess_start}<br />{p.real_end || p.sess_end}</> : "—"}
                </div>
                <div className="w-20 shrink-0 text-right text-xs" data-testid={`prod-timeline-breakcount-${p.key}`}>
                  {p.break_count ? (
                    <>
                      <div className="font-medium text-slate-700 dark:text-slate-200">{p.break_count}</div>
                      <div className="text-[11px] text-slate-400">{p.break_hms}</div>
                    </>
                  ) : <span className="text-slate-300">—</span>}
                </div>
                <div className="flex w-40 shrink-0 items-center gap-2 pl-3">
                  <div className="text-xs">
                    <div className="font-medium text-slate-700 dark:text-slate-200">{p.active_hms}</div>
                    <div className="text-emerald-600 dark:text-emerald-400">{p.productive_hms}</div>
                  </div>
                  <Sparkline data={p.hourly} />
                </div>
              </button>
              {open === p.key && (
                <ProdTimelineDetail person={p} day={days[`${p.key}|${date}`]} detailMin={detailMin} showIdle={showIdle} />
              )}
            </div>
          ))}
        </div>
      </Card>

      <p className="mt-2 text-xs text-slate-400">
        Click a person to open their whole day — breaks and the apps behind the bands. Bands are coloured by
        this app&apos;s own classification, not the tool&apos;s. Times are America/Edmonton.
        {data?.cached === false ? " Freshly fetched." : ""}
      </p>
    </div>
  );
}
